import { useRef, useState } from 'react';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { useCreatePropertyMutation } from '@/components/properties/services/propertyApi';
import { useAddPropertiesToCart } from '@/components/properties/property-view/hooks/useAddPropertiesToCart';
import type { PropertyEntity } from '@/types/property.types';
import {
  NewPropertyForm,
  buildPropertyRequest,
  type NewPropertySubmitValues,
} from './NewPropertyForm';

interface AddNewPropertyViewProps {
  onBack: () => void;
  onCreated: (property: PropertyEntity) => void;
  /**
   * Reports a failure to the parent so it can surface the error banner.
   * `retryPayload` is only meaningful for the create-succeeded /
   * add-to-cart-failed path — the banner's "Try again" button replays
   * the add-to-cart call against the just-created property. For
   * create-failures it's omitted, since the right way to retry is for
   * the user to click "Create Property and Add to Cart" again with
   * their form contents intact.
   */
  onError: (
    title: string,
    detail: string,
    retryPayload?: {
      propertyId: string;
      propertyAddress: string;
      createdNewProperty: boolean;
    }
  ) => void;
  /** Forwarded from NewOrderDrawer so it can prompt before discarding
   *  an in-progress edit when the user hits Esc or the back arrow. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Shared banner element rendered above the form so create / cart
   *  errors stay visible without forcing the user off the form. */
  bannerNode?: React.ReactNode;
}

const formatAddress = (p: PropertyEntity) =>
  `${p.address1} · ${p.city}, ${p.state} ${p.zipCode}`;

export function AddNewPropertyView({
  onBack,
  onCreated,
  onError,
  onDirtyChange,
  bannerNode,
}: AddNewPropertyViewProps) {
  const [createProperty] = useCreatePropertyMutation();
  const addToCart = useAddPropertiesToCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const triggerSubmit = () => {
    formRef.current?.requestSubmit();
  };

  const handleSubmit = async (values: NewPropertySubmitValues) => {
    setIsSubmitting(true);
    let created: PropertyEntity | undefined;
    try {
      created = await createProperty(buildPropertyRequest(values)).unwrap();
    } catch (err) {
      setIsSubmitting(false);
      // No retryPayload — the property hasn't been created yet, so the
      // banner shouldn't show a Try Again button. The user retries by
      // clicking "Create Property and Add to Cart" again with the
      // current form contents (which are preserved because the view
      // doesn't navigate away on error).
      onError(
        "Couldn't create property",
        err instanceof Error ? err.message : 'Unknown error',
      );
      return;
    }
    try {
      await addToCart([created.propId]);
      onCreated(created);
    } catch (err) {
      onError(
        "Couldn't add to cart",
        err instanceof Error ? err.message : 'Unknown error',
        {
          propertyId: created.propId,
          propertyAddress: formatAddress(created),
          createdNewProperty: true,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-3 border-b border-border px-8 py-6 max-lg:px-5">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:h-11 max-lg:w-11"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <Typography as="h2" className="text-2xl font-semibold text-foreground">
            Add a New Property
          </Typography>
          <p className="mt-1 text-sm text-muted-foreground">
            We'll save this to your property list and add it to your cart.
          </p>
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto px-8 py-8 max-lg:px-5"
        ref={(el) => {
          // Capture the inner <form> element so the footer button can
          // trigger requestSubmit() — keeps Formik in charge of validation
          // while the Create button lives outside the <form>.
          formRef.current = el?.querySelector('form') ?? null;
        }}
      >
        {bannerNode}
        <NewPropertyForm
          onSubmit={handleSubmit}
          onCancel={onBack}
          isSubmitting={isSubmitting}
          onDirtyChange={onDirtyChange}
        />
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-card px-8 py-5 max-lg:px-5">
        <Button type="button" variant="ghost" onClick={onBack} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="button" onClick={triggerSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <ShoppingCart className="h-4 w-4" />
          )}
          Create Property and Add to Cart
        </Button>
      </div>
    </div>
  );
}
